import { useGoogleLogin } from '@react-oauth/google';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { useToast } from '@/hooks/useToast';
import { setCookie } from '@/lib/utils';
import { safeAuthRedirect } from '@/lib/auth-redirect';
import { getAuthToken, getAuthTokenWithInviteCode } from '@/lib/apis';
import { getUserProfileFromToken } from '@/lib/auth';
import { useUserProfile } from '@/contexts/user.context';
import { googleSignupScopesQueryOptions } from '@/lib/query-options';

const ADWORDS_SCOPE = 'https://www.googleapis.com/auth/adwords';

const useGoogleSignUp = ({
  inviteCode,
}: {
  redirectUrl?: string;
  inviteCode?: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useUserProfile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const posthog = usePostHog();
  // The backend owns the scope list (#6879): Google Ads only, plus Analytics and Tag Manager behind its flag.
  const { data: consent } = useSuspenseQuery(googleSignupScopesQueryOptions());

  const signupWithGoogle = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      // Google presents the Ads scope as an unticked opt-in checkbox on the consent
      // screen. Catch a skipped checkbox here, before the auth-code exchange (#5901).
      if (!codeResponse.scope?.includes(ADWORDS_SCOPE)) {
        toast({
          title: 'Google Ads Permission Required',
          description:
            'Google Ads access was not granted. Click "Sign up with Google" again and tick the Google Ads checkbox on the Google consent screen.',
        });
        return;
      }
      setIsLoading(true);
      try {
        const token = inviteCode
          ? await getAuthTokenWithInviteCode({ authCode: codeResponse.code, inviteCode })
          : await getAuthToken({ authCode: codeResponse.code });

        if (token) {
          setCookie('access_token', token, 1);
          const userProfile = getUserProfileFromToken(token);
          setUser(userProfile);
          const { name, email } = userProfile;
          posthog?.identify(email, {
            email,
            name,
          });
          posthog?.capture('registration_completed', { method: 'google' });

          // Resume where the sign-up interrupted (#6377): same validated
          // redirect contract as sign-in, so a first-time user completing the
          // OAuth consent flow lands back on the consent screen.
          const target = safeAuthRedirect();
          if (target) {
            window.location.assign(target);
            return;
          }
          // Just navigate to a default authenticated route
          // The _authenticated.tsx will handle checking for pending invites
          navigate({ to: '/business' });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unable to sign in due to an internal error. Please try again.';
        toast({
          title: 'SignUp Failed',
          description: errorMessage,
        });
        navigate({ to: '/signup' });
      } finally {
        setIsLoading(false);
      }
    },
    scope: consent.scope,
    flow: 'auth-code',
    include_granted_scopes: false,
  });

  return { signupWithGoogle, isLoading };
};

export default useGoogleSignUp;
